function normalizeKeys(data) {
  if (!data) return data;
  const keyMap = {
    resName: "ResName",
    address: "Address",
    selectedAdjectives: "SelectedAdjectives",
    selectedRoles: "SelectedRoles",
    careerInterest: "CareerInterest",
    personalInterest: "PersonalInterest",
    hardSkill: "HardSkill",
    technicalSkill: "HardSkill",
    softSkill: "SoftSkill",
    licensesCert: "LicensesCert",
    language: "Language",
    professionalOrganization: "ProfessionalOrganization",
    volunteerOrganizations: "VolunteerOrganizations",
    education: "Education",
    work: "Work",
    targetIndustry: "TargetIndustry",
    targetCompany: "TargetCompany",
    strengths: "Strengths",
    finalInfo: "FinalInfo",
    smallWins: "SmallWins",
    fitPreferences: "FitPreferences",
  };
  const normalized = {};
  for (const [key, value] of Object.entries(data)) {
    const newKey = keyMap[key] || key;
    normalized[newKey] = value;
  }
  return normalized;
}

export function cleanProfileData(data) {
  if (!data) return null;

  const cleaned = JSON.parse(JSON.stringify(normalizeKeys(data)));

  // Cosmos DB
  delete cleaned._rid;
  delete cleaned._self;
  delete cleaned._etag;
  delete cleaned._attachments;
  delete cleaned._ts;
  delete cleaned.id;
  delete cleaned.typeUniqueKey;
  delete cleaned.userId;
  delete cleaned.objectType;
  delete cleaned.environment;
  delete cleaned.dateCreated;

  // Strip IDs from SelectedRoles
  if (cleaned.SelectedRoles?.Roles) {
    cleaned.SelectedRoles.Roles = cleaned.SelectedRoles.Roles.map((r) => ({
      text: r.text,
    }));
  }

  // Strip IDs from HardSkills and replace level with label
  if (cleaned.HardSkill?.HardSkills) {
    const levelLabels = { 1: "Advanced", 2: "Familiar", 3: "Basic" };
    cleaned.HardSkill.HardSkills = cleaned.HardSkill.HardSkills.map((s) => ({
      text: s.text,
      level: levelLabels[s.level] || s.level,
    }));
  }

  // Remove HardSkillLabels since we're using text labels now
  if (cleaned.HardSkill?.HardSkillLabels) {
    delete cleaned.HardSkill.HardSkillLabels;
  }

  // Strip IDs from SoftSkills
  if (cleaned.SoftSkill?.SoftSkills) {
    cleaned.SoftSkill.SoftSkills = cleaned.SoftSkill.SoftSkills.map((s) => ({
      text: s.text,
    }));
  }

  // Strip IDs from Languages
  if (cleaned.Language?.Languages) {
    cleaned.Language.Languages = cleaned.Language.Languages.map((l) => ({
      text: l.text,
      level: l.level,
    }));
  }

  // Strip IDs from LicensesCerts
  if (cleaned.LicensesCert?.LicensesCerts) {
    cleaned.LicensesCert.LicensesCerts = cleaned.LicensesCert.LicensesCerts.map(
      (l) => ({
        text: l.text,
      })
    );
  }

  // Include structured credentials
  if (cleaned.LicensesCert?.Credentials) {
    cleaned.LicensesCert.Credentials = cleaned.LicensesCert.Credentials.map(
      (c) => ({
        name: c.name,
        credentialType: c.credentialType,
        issuingAuthority: c.issuingAuthority,
        jurisdiction: c.jurisdiction,
        jurisdictionType: c.jurisdictionType,
        issueDate: c.issueDate,
        expirationDate: c.expirationDate,
        doesNotExpire: c.doesNotExpire,
        status: c.status,
        associatedSkills: c.associatedSkills || [],
      })
    );
  }

  // Strip IDs from CareerInterests
  if (cleaned.CareerInterest?.CareerInterests) {
    cleaned.CareerInterest.CareerInterests = cleaned.CareerInterest.CareerInterests.map(
      (c) => ({
        text: c.text,
      })
    );
  }

  // Strip IDs from PersonalInterests
  if (cleaned.PersonalInterest?.PersonalInterests) {
    cleaned.PersonalInterest.PersonalInterests = cleaned.PersonalInterest.PersonalInterests.map(
      (p) => ({
        text: p.text,
      })
    );
  }

  // Strip IDs from ProfessionalOrg
  if (cleaned.ProfessionalOrganization?.ProfessionalOrg) {
    cleaned.ProfessionalOrganization.ProfessionalOrg = cleaned.ProfessionalOrganization.ProfessionalOrg.map(
      (p) => ({
        text: p.text,
      })
    );
  }

  // Strip IDs from VolunteerOrganizations
  if (cleaned.VolunteerOrganizations?.VolunteerOrg) {
    cleaned.VolunteerOrganizations.VolunteerOrg = cleaned.VolunteerOrganizations.VolunteerOrg.map(
      (v) => ({
        text: v.text,
      })
    );
  }

  // Convert Education entries from dict to array
  if (cleaned.Education?.EducationDict) {
    const eduArray = Object.values(cleaned.Education.EducationDict)
      .map((edu) => {
        const cleanedEdu = { ...edu };
        delete cleanedEdu.key;
        return cleanedEdu;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    cleaned.Education.EducationDict = eduArray;
  }

  // Convert Work entries from dict to array
  if (cleaned.Work?.WorkDict) {
    const workArray = Object.values(cleaned.Work.WorkDict)
      .map((work) => {
        const cleanedWork = { ...work };

        // Strip keys from positions
        if (cleanedWork.workRole?.positions) {
          cleanedWork.workRole.positions = cleanedWork.workRole.positions.map(
            (p) => ({
              title: p.title,
              role: p.role,
            })
          );
        }

        // Strip IDs from achievements
        if (cleanedWork.achievementList) {
          cleanedWork.achievementList = cleanedWork.achievementList.map(
            (a) => ({
              background: a.background,
              responsibility: a.responsibility,
              actionResult: a.actionResult,
              measurement: a.measurement,
              displayAuto: a.displayAuto,
              displayCustom: a.displayCustom,
              useCustom: a.useCustom,
            })
          );
        }

        return cleanedWork;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    cleaned.Work.WorkDict = workArray;
  }

  // Strip IDs from Adjectives
  if (cleaned.SelectedAdjectives?.Adjectives) {
    cleaned.SelectedAdjectives.Adjectives = cleaned.SelectedAdjectives.Adjectives.map(
      (a) => ({
        text: a.text,
      })
    );
  }

  // Clean SmallWins
  if (cleaned.SmallWins) {
    delete cleaned.SmallWins._rid;
    delete cleaned.SmallWins._self;
    delete cleaned.SmallWins._etag;
    delete cleaned.SmallWins._attachments;
    delete cleaned.SmallWins._ts;
    delete cleaned.SmallWins.id;
    delete cleaned.SmallWins.typeUniqueKey;
    delete cleaned.SmallWins.userId;
    delete cleaned.SmallWins.objectType;

    if (cleaned.SmallWins.items) {
      cleaned.SmallWins.items = cleaned.SmallWins.items.map((item) => ({
        description: item.description,
        context: item.context,
        action: item.action,
        outcome: item.outcome,
        otherNotes: item.otherNotes,
        category: item.category,
        assignedToType: item.assignedToType,
        assignedToLabel: item.assignedToLabel,
        eventMonth: item.eventMonth,
        eventYear: item.eventYear,
        tags: item.tags || [],
        links: item.links || [],
      }));
    }
  }

  // Clean FitPreferences
  if (cleaned.FitPreferences) {
    delete cleaned.FitPreferences._rid;
    delete cleaned.FitPreferences._self;
    delete cleaned.FitPreferences._etag;
    delete cleaned.FitPreferences._attachments;
    delete cleaned.FitPreferences._ts;
    delete cleaned.FitPreferences.id;
    delete cleaned.FitPreferences.typeUniqueKey;
    delete cleaned.FitPreferences.userId;
    delete cleaned.FitPreferences.objectType;

    if (cleaned.FitPreferences.answers) {
      cleaned.FitPreferences.answers = cleaned.FitPreferences.answers.map(
        (answer) => ({
          questionId: answer.questionId,
          selectedOptions: (answer.selectedOptions || []).map((opt) => ({
            label: opt.label,
          })),
        })
      );
    }
  }

  return cleaned;
}

export function cleanProfileDataSimplified(rawData) {
  if (!rawData) {
    return null;
  }

  const data = normalizeKeys(rawData);
  let name = "";

  if (data.ResName?.displayValue) {
    name = data.ResName.displayValue;
  } else if (data.ResName) {
    const parts = [
      data.ResName.firstName,
      data.ResName.middleName,
      data.ResName.lastName,
      data.ResName.suffix,
    ].filter(Boolean);
    name = parts.join(" ").trim();
  }

  if (!name && data.Address?.email) {
    name = data.Address.email.split("@")[0];
  }

  if (!name && data.userId) {
    name = data.userId.split("@")[0];
  }

  const cleaned = {
    name: name,
    contact: {
      email: data.Address?.email || "",
      phone: data.Address?.phoneNumber || "",
      location: data.Address?.displayValue || "",
      addressLine1: data.Address?.addressLine1 || "",
      addressLine2: data.Address?.addressLine2 || "",
      city: data.Address?.city || "",
      state: data.Address?.state || "",
      country: data.Address?.country || "",
      zipCode: data.Address?.zipCode || "",
      linkedin: data.Address?.linkedin || "",
      otherLink: data.Address?.otherLink || "",
    },
    roles: (data.SelectedRoles?.Roles || [])
      .map((r) => r?.text || "")
      .filter(Boolean),
    technicalSkills: {
      advanced: [],
      familiar: [],
      basic: [],
    },
    leadershipSkills: (data.SoftSkill?.SoftSkills || [])
      .map((s) => s?.text || "")
      .filter(Boolean),
    languages:
      data.Language?.LanguagesDisplay?.split(", ").filter(Boolean) || [],
    licenses:
      data.LicensesCert?.LicensesCertsDisplay?.split(", ").filter(Boolean) ||
      [],
    credentials: (data.LicensesCert?.Credentials || []).map((c) => ({
      name: c.name,
      credentialType: c.credentialType,
      issuingAuthority: c.issuingAuthority,
      jurisdiction: c.jurisdiction,
      jurisdictionType: c.jurisdictionType,
      status: c.status,
      associatedSkills: c.associatedSkills || [],
    })),
    professionalOrganizations:
      data.ProfessionalOrganization?.ProfessionalOrgDisplay?.split(", ").filter(
        Boolean
      ) || [],
    volunteerOrganizations:
      data.VolunteerOrganizations?.VolunteerOrgDisplay?.split(", ").filter(
        Boolean
      ) || [],
    interests:
      data.PersonalInterest?.PersonalInterestsDisplay?.split(", ").filter(
        Boolean
      ) || [],
    education: [],
    experience: [],
    professionalSummary: data.FinalInfo?.professionalSummary || "",
    otherSkills: data.FinalInfo?.otherSkills || [],
  };

  // Organize technical skills by level
  (data.HardSkill?.HardSkills || []).forEach((skill) => {
    if (skill.level === 1) {
      cleaned.technicalSkills.advanced.push(skill.text);
    } else if (skill.level === 2) {
      cleaned.technicalSkills.familiar.push(skill.text);
    } else if (skill.level === 3) {
      cleaned.technicalSkills.basic.push(skill.text);
    }
  });

  // Clean education data
  if (data.Education?.EducationDict) {
    cleaned.education = Object.values(data.Education.EducationDict)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((edu) => ({
        school: edu.educationGeneral?.universityName || "",
        location: `${edu.educationGeneral?.universityCity || ""}, ${edu
          .educationGeneral?.universityState || ""}`
          .trim()
          .replace(/^,\s*|,\s*$/g, ""),
        degree: edu.educationDegree?.degreeName || "",
        area: edu.educationDegree?.degreeArea || "",
        gpa: edu.educationGPA?.gpa || "",
        graduated: edu.educationGeneral?.graduated || false,
        accolades: edu.educationAccolades || [],
      }));
  }

  // Clean work experience data
  if (data.Work?.WorkDict) {
    cleaned.experience = Object.values(data.Work.WorkDict)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((work) => ({
        company: work.workGeneral?.companyName || "",
        location: `${work.workGeneral?.companyCity || ""}, ${work.workGeneral
          ?.companyState || ""}`
          .trim()
          .replace(/^,\s*|,\s*$/g, ""),
        positions: (work.workRole?.positions || []).map((p) => p.title),
        currentlyWorking: work.workGeneral?.workingCurrently || false,
        responsibilities: work.workResponsibilities?.description || "",
        achievements: (work.achievementList || [])
          .map((a) => (a.useCustom ? a.displayCustom : a.displayAuto))
          .filter(Boolean),
      }));
  }

  return cleaned;
}

export function cleanProfileDataFromStore(storeData) {
  const {
    nameDisplay,
    addressDisplay,
    email,
    phoneNumber,
    linkedin,
    otherLink,
    selectedRoles,
    hardSkills,
    softSkills,
    languagesDisplay,
    licensesCertsDisplay,
    professionalOrgDisplay,
    volunteerOrgDisplay,
    personalInterestsDisplay,
    eduList,
    workList,
    professionalSummary,
    otherSkills,
  } = storeData;

  // Build data object in the API format, then use cleanProfileData
  const apiFormatData = {
    ResName: { displayValue: nameDisplay || "" },
    Address: {
      email: email || "",
      phoneNumber: phoneNumber || "",
      displayValue: addressDisplay || "",
      linkedin: linkedin || "",
      otherLink: otherLink || "",
    },
    SelectedRoles: {
      Roles: Array.isArray(selectedRoles) ? selectedRoles : [],
    },
    HardSkill: {
      HardSkills: Array.isArray(hardSkills) ? hardSkills : [],
    },
    SoftSkill: {
      SoftSkills: Array.isArray(softSkills) ? softSkills : [],
    },
    Language: {
      LanguagesDisplay: languagesDisplay || "",
    },
    LicensesCert: {
      LicensesCertsDisplay: licensesCertsDisplay || "",
    },
    ProfessionalOrganization: {
      ProfessionalOrgDisplay: professionalOrgDisplay || "",
    },
    VolunteerOrganizations: {
      VolunteerOrgDisplay: volunteerOrgDisplay || "",
    },
    PersonalInterest: {
      PersonalInterestsDisplay: personalInterestsDisplay || "",
    },
    Education: {
      EducationDict: {},
    },
    Work: {
      WorkDict: {},
    },
    FinalInfo: {
      professionalSummary: professionalSummary || "",
      otherSkills: Array.isArray(otherSkills) ? otherSkills : [],
    },
  };

  // Transform education list to EducationDict format
  if (Array.isArray(eduList)) {
    eduList.forEach((edu, index) => {
      const key = edu.key || `edu-${index}`;
      apiFormatData.Education.EducationDict[key] = {
        educationGeneral: {
          universityName: edu.school || edu.general || "",
          universityCity: edu.location?.split(",")[0]?.trim() || "",
          universityState: edu.location?.split(",")[1]?.trim() || "",
          graduated: edu.graduated || false,
        },
        educationDegree: {
          degreeName: edu.degree || "",
          degreeArea: edu.area || edu.degreeMinor || "",
        },
        educationGPA: {
          gpa: edu.gpa || "",
        },
        educationAccolades: edu.accolades || [],
        order: edu.order !== undefined ? edu.order : index,
      };
    });
  }

  // Transform work list to WorkDict format
  if (Array.isArray(workList)) {
    workList.forEach((work, index) => {
      const key = work.key || `work-${index}`;
      apiFormatData.Work.WorkDict[key] = {
        workGeneral: {
          companyName: work.company || work.general || "",
          companyCity: work.location?.split(",")[0]?.trim() || "",
          companyState: work.location?.split(",")[1]?.trim() || "",
          workingCurrently: work.currentlyWorking || false,
        },
        workRole: {
          positions: (work.positions || work.roles || []).map((title) =>
            typeof title === "string" ? { title } : title
          ),
        },
        workResponsibilities: {
          description: work.responsibilities || "",
        },
        achievementList: (work.achievements || []).map((achievement) => ({
          useCustom: true,
          displayCustom: achievement,
          displayAuto: achievement,
        })),
        order: work.order !== undefined ? work.order : index,
      };
    });
  }

  return cleanProfileDataSimplified(apiFormatData);
}

export function downloadProfileAsJson(cleanedData, fileName) {
  const blob = new Blob([JSON.stringify(cleanedData, null, 2)], {
    type: "application/json",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName} Profile.json`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 7000);
}

export async function exportAllEmployeeProfiles() {
  try {
    const response = await fetch("/api/GetAllEmployees");
    if (!response.ok) {
      return {
        success: false,
        profileCount: 0,
        failedCount: 0,
        error: "Failed to load employees",
      };
    }

    const employeesResult = await response.json();
    const employees = employeesResult.data?.employees || [];

    const employeesWithResumes = employees.filter(
      (emp) => emp.hasResume && emp.latestResumeId
    );

    if (employeesWithResumes.length === 0) {
      return {
        success: false,
        profileCount: 0,
        failedCount: 0,
        error: "No employees with resumes found",
      };
    }

    const profiles = [];
    const failedProfiles = [];

    for (const employee of employeesWithResumes) {
      try {
        const resumeResponse = await fetch(
          `/api/GetEmployeeResume?employeeId=${employee.id}&resumeId=${employee.latestResumeId}`
        );

        if (resumeResponse.ok) {
          const result = await resumeResponse.json();
          const cleanedProfile = cleanProfileData(result.data);

          if (
            cleanedProfile &&
            cleanedProfile.ResName &&
            ((Array.isArray(cleanedProfile.Work?.WorkDict) &&
              cleanedProfile.Work.WorkDict.length > 0) ||
              (Array.isArray(cleanedProfile.Education?.EducationDict) &&
                cleanedProfile.Education.EducationDict.length > 0))
          ) {
            profiles.push(cleanedProfile);
          } else {
            failedProfiles.push({
              status: "Failed",
              name: employee.name || employee.email || "Unknown",
              email: employee.email || "",
              reason: "Incomplete resume data",
            });
          }
        } else {
          // API call failed
          failedProfiles.push({
            status: "Failed",
            name: employee.name || employee.email || "Unknown",
            email: employee.email || "",
            reason: "Failed to fetch resume data",
          });
        }
      } catch (error) {
        // Error during processing
        failedProfiles.push({
          status: "Failed",
          name: employee.name || employee.email || "Unknown",
          email: employee.email || "",
          reason: error.message || "Unknown error",
        });
        console.error(
          `Error fetching resume for employee ${employee.id}:`,
          error
        );
      }
    }

    if (profiles.length === 0 && failedProfiles.length === 0) {
      return {
        success: false,
        profileCount: 0,
        failedCount: 0,
        error: "No profiles could be exported",
      };
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      totalProfiles: profiles.length,
      failedProfiles: failedProfiles.length,
      profiles: profiles,
    };

    // Include failed profiles if any
    if (failedProfiles.length > 0) {
      exportData.failed = failedProfiles;
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split("T")[0];
    link.download = `Employee_Profiles_Export_${timestamp}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 7000);

    return {
      success: true,
      profileCount: profiles.length,
      failedCount: failedProfiles.length,
    };
  } catch (error) {
    return {
      success: false,
      profileCount: 0,
      failedCount: 0,
      error: error.message,
    };
  }
}
